import { lerp, outBackSoft } from "./ease";
import { place, type Vec } from "./geometry";
import { ms } from "../game/speed";

/**
 * پروازِ کارت — پورتِ FlyCard در HokmView/GameView/CharBargView.
 *
 * چرا JS و نه CSS: چهار ویژگیِ این پرواز با هم در یک keyframe نمی‌گنجند —
 * مسیرِ LerpUnclamped با OutBackSoft (از هدف رد می‌شود و برمی‌گردد)، چرخشِ
 * ۱۸۰ درجه تا زاویه‌ی نهایی، مقیاسِ ۱٫۱۲ تا ۱، و مهم‌تر از همه: باید بشود
 * **منتظرش ماند**، چون صحنه‌ی یونیتی همه‌چیز را پشتِ هم می‌چیند و وضعیت را
 * فقط بعد از فرود می‌نشانَد.
 *
 * زمان از `ms()` می‌گذرد — همان Speed.Sec یونیتی.
 */
export function flyCard(el: HTMLElement, from: Vec, to: Vec, endRot: number, durMs: number): Promise<void> {
  const dur = Math.max(1, ms(durMs));
  return new Promise((resolve) => {
    const t0 = performance.now();
    const step = (now: number) => {
      if (!el.isConnected) { resolve(); return; }
      const t = Math.min(1, (now - t0) / dur);
      const p = outBackSoft(t);
      const pos = { x: lerp(from.x, to.x, p), y: lerp(from.y, to.y, p) };
      el.style.transform = place(pos, endRot + (1 - p) * 180, 1.12 - 0.12 * p);
      if (t < 1) requestAnimationFrame(step);
      else { el.style.transform = place(to, endRot, 1); resolve(); }
    };
    el.style.transform = place(from, endRot + 180, 1.12);
    requestAnimationFrame(step);
  });
}

/** WaitForSeconds(Speed.Sec(x)) — انتظارِ صحنه، با ضریبِ سرعت. */
export const wait = (durMs: number): Promise<void> =>
  new Promise((r) => setTimeout(r, ms(durMs)));

/**
 * یک حلقه‌ی رندرِ زمان‌محور: هر فریم k ∈ [۰..۱] را می‌دهد. برای افکت‌هایی که
 * در یونیتی با `for (t < dur) … yield return null` نوشته شده‌اند.
 */
export function tween(durMs: number, frame: (k: number) => void): Promise<void> {
  const dur = Math.max(1, ms(durMs));
  return new Promise((resolve) => {
    const t0 = performance.now();
    const step = (now: number) => {
      // کفِ صفر لازم است: تایم‌استمپی که rAF می‌دهد لحظه‌ی **شروعِ فریم**
      // است و می‌تواند از `performance.now()` ِ لحظه‌ی ساختِ تویین عقب‌تر
      // باشد — به‌ویژه وقتی تویین از داخلِ یک فریمِ rAF ساخته شود. آن‌وقت
      // k منفی می‌شد، هیچ‌وقت به ۱ نمی‌رسید، و تویین برای همیشه یخ می‌زد.
      const k = Math.min(1, Math.max(0, (now - t0) / dur));
      frame(k);
      if (k < 1) requestAnimationFrame(step); else resolve();
    };
    requestAnimationFrame(step);
  });
}

/**
 * کارتِ پرنده: عنصری موقتی در لایه‌ی پرواز که بعد از فرود دور می‌رود —
 * همان `CardFace(_flyLayer, …)` + `Destroy` در یونیتی.
 */
export function spawn(layer: HTMLElement, className: string, widthPx: number, inner?: (el: HTMLElement) => void): HTMLElement {
  const el = document.createElement("div");
  el.className = className;
  el.style.position = "absolute";
  el.style.left = "50%";
  el.style.top = "50%";
  el.style.setProperty("--cw", `${widthPx}px`);
  el.style.pointerEvents = "none";
  inner?.(el);
  layer.appendChild(el);
  return el;
}

export const kill = (el: HTMLElement | null | undefined) => { el?.remove(); };

/**
 * همان tween/wait ولی **بی ضریبِ سرعت** — برای افکت‌هایی که یونیتی با
 * Time.deltaTime/unscaledDeltaTime و عددِ خام می‌زند (Pulse، نمادِ وسط، چکش،
 * لرزش). ضریبِ سرعت مالِ حرکتِ کارت‌هاست، نه جلوه‌ها؛ وگرنه با «خیلی آرام»
 * یک پالسِ نیم‌ثانیه‌ای یک‌ثانیه‌ونیم طول می‌کشید.
 */
export function tweenRaw(durMs: number, frame: (k: number) => void): Promise<void> {
  const dur = Math.max(1, durMs);
  return new Promise((resolve) => {
    const t0 = performance.now();
    const step = (now: number) => {
      // کفِ صفر لازم است: تایم‌استمپی که rAF می‌دهد لحظه‌ی **شروعِ فریم**
      // است و می‌تواند از `performance.now()` ِ لحظه‌ی ساختِ تویین عقب‌تر
      // باشد — به‌ویژه وقتی تویین از داخلِ یک فریمِ rAF ساخته شود. آن‌وقت
      // k منفی می‌شد، هیچ‌وقت به ۱ نمی‌رسید، و تویین برای همیشه یخ می‌زد.
      const k = Math.min(1, Math.max(0, (now - t0) / dur));
      frame(k);
      if (k < 1) requestAnimationFrame(step); else resolve();
    };
    requestAnimationFrame(step);
  });
}

export const waitRaw = (durMs: number): Promise<void> => new Promise((r) => setTimeout(r, durMs));
